import React, { useState, useEffect, useRef, useCallback } from "react";
import { X, CheckCircle, Barbell, FloppyDisk } from "@phosphor-icons/react";
import api from "../lib/api";
import { paceLabel } from "../lib/pace";
import { toast } from "sonner";
import LiveSessionRestPhase from "./live/LiveSessionRestPhase";
import ExerciseHowToModal from "./live/ExerciseHowToModal";

function beep(duration = 200, frequency = 880, volume = 0.3) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = frequency;
    osc.type = "sine";
    gain.gain.value = volume;
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + duration / 1000);
    setTimeout(() => ctx.close(), duration + 50);
  } catch (e) {
    /* silent */
  }
}

const RPE_LABELS = {
  1: "Muy fácil",
  2: "Fácil",
  3: "Fácil",
  4: "Moderado",
  5: "Moderado",
  6: "Algo difícil",
  7: "Difícil",
  8: "Muy difícil",
  9: "Casi máximo",
  10: "Máximo",
};

export default function LiveSession({ routine, userId, onClose }) {
  const exercises = routine.exercises || [];
  const [exIdx, setExIdx] = useState(0);
  const [setIdx, setSetIdx] = useState(0);
  const [phase, setPhase] = useState("working"); // working | logging | micro_rest | macro_rest | done
  const [elapsed, setElapsed] = useState(0);
  const [paused, setPaused] = useState(false);
  const [rpe, setRpe] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [logs, setLogs] = useState([]); // set logs collected during session
  const [sessionId, setSessionId] = useState(null);
  const [imgBroken, setImgBroken] = useState(false);
  const [showHowTo, setShowHowTo] = useState(false);
  const [sessionStartedAt] = useState(() => new Date().toISOString());
  const timerRef = useRef(null);
  const startedRef = useRef(false);

  const current = exercises[exIdx] || null;
  const totalSets = current?.sets || 0;
  const micro = current?.micro_pause_sec || 0;
  const macro = current?.macro_pause_sec || 0;

  const restDuration = phase === "micro_rest" ? micro : phase === "macro_rest" ? macro : 0;
  const remaining = Math.max(0, restDuration - elapsed);

  // Start session (backend record)
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    (async () => {
      try {
        const { data } = await api.post(`/users/${userId}/sessions`, {
          routine_id: routine.routine_id,
          routine_name: routine.name,
        });
        setSessionId(data.session_id);
      } catch {
        /* non-fatal */
      }
    })();
  }, [userId, routine]);

  // Reset el flag de imagen rota al cambiar de ejercicio
  useEffect(() => {
    setImgBroken(false);
  }, [exIdx]);

  // Rest timer tick
  useEffect(() => {
    if (paused || phase === "working" || phase === "logging" || phase === "done") {
      clearInterval(timerRef.current);
      return;
    }
    const id = setInterval(() => setElapsed((e) => e + 1), 1000);
    timerRef.current = id;
    return () => clearInterval(id);
  }, [paused, phase]);

  const advance = useCallback(() => {
    setElapsed(0);
    if (phase === "micro_rest") {
      setSetIdx((s) => s + 1);
      setPhase("working");
    } else if (phase === "macro_rest") {
      setExIdx((i) => i + 1);
      setSetIdx(0);
      setPhase("working");
    }
  }, [phase]);

  // Auto-advance when rest ends
  useEffect(() => {
    if ((phase === "micro_rest" || phase === "macro_rest") && elapsed >= restDuration && restDuration > 0) {
      beep(300, 900);
      setTimeout(() => beep(400, 1200), 250);
      advance();
    }
  }, [elapsed, phase, restDuration, advance]);

  const saveSessionToServer = async (aborted = false, finalLogs = logs) => {
    if (!sessionId) return;
    try {
      await api.put(`/sessions/${sessionId}/complete`, {
        ended_at: new Date().toISOString(),
        sets: finalLogs,
        aborted,
      });
    } catch {
      /* silent */
    }
  };

  const handleClose = async () => {
    if (phase !== "done" && logs.length > 0) {
      await saveSessionToServer(true);
    }
    onClose();
  };

  const startSetLog = () => {
    beep(150, 600);
    setRpe(null);
    setNoteText("");
    setPhase("logging");
  };

  const confirmSetLog = () => {
    const newLog = {
      exercise_id: current.exercise_id,
      exercise_name: current.exercise_name,
      set_number: setIdx + 1,
      weight_kg: Number(current.weight_kg) || 0,
      reps: Number(current.reps) || 0,
      distance_m: Number(current.distance_m) || 0,
      duration_min: Number(current.duration_min) || 0,
      rpe: rpe != null ? Number(rpe) : null,
      notes: noteText || null,
      completed_at: new Date().toISOString(),
    };
    const newLogs = [...logs, newLog];
    setLogs(newLogs);

    // Decide next phase
    if (setIdx + 1 < totalSets) {
      if (micro > 0) {
        setPhase("micro_rest");
        setElapsed(0);
      } else {
        setSetIdx((s) => s + 1);
        setPhase("working");
      }
    } else {
      if (exIdx + 1 >= exercises.length) {
        // Routine complete
        setPhase("done");
        beep(500, 1200);
        setTimeout(() => beep(500, 1500), 300);
        saveSessionToServer(false, newLogs);
      } else if (macro > 0) {
        setPhase("macro_rest");
        setElapsed(0);
      } else {
        setExIdx((i) => i + 1);
        setSetIdx(0);
        setPhase("working");
      }
    }
  };

  const skipRest = () => {
    if (phase === "micro_rest" || phase === "macro_rest") advance();
  };

  const totalExercises = exercises.length;
  const progressPct = phase === "done"
    ? 100
    : Math.round(((exIdx + (setIdx / Math.max(totalSets, 1))) / Math.max(totalExercises, 1)) * 100);

  const rpeButtons = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  const rpeColor = (n) => {
    if (n <= 3) return "#32D74B";
    if (n <= 6) return "#FFD60A";
    if (n <= 8) return "#FF9500";
    return "#FF3B30";
  };

  return (
    <div className="fixed inset-0 z-50 bg-app flex flex-col notranslate" translate="no" data-testid="live-session">
      <div className="flex items-center justify-between px-4 sm:px-8 py-4 border-b border-app">
        <div className="flex items-center gap-3">
          <Barbell size={24} weight="fill" className="text-primary-app" />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted">Sesión en vivo</div>
            <div className="font-display font-black uppercase text-lg tracking-tight">{routine.name}</div>
          </div>
        </div>
        <button onClick={handleClose} className="btn-ghost flex items-center gap-2" data-testid="live-close">
          <X size={18} weight="bold" /> Salir
        </button>
      </div>

      <div className="h-1 bg-[#262626]">
        <div className="h-full bg-[#FF3B30] transition-all" style={{ width: `${progressPct}%` }} data-testid="live-progress" />
      </div>

      <div className="flex-1 overflow-y-auto">
        {phase === "done" ? (
          <div className="h-full flex flex-col items-center justify-center p-8 text-center">
            <CheckCircle size={120} weight="duotone" className="text-[#32D74B] mb-6" />
            <h2 className="font-display font-black uppercase text-5xl tracking-tighter mb-4">¡Rutina Completa!</h2>
            <p className="text-muted mb-2">Completaste {exercises.length} {exercises.length === 1 ? "ejercicio" : "ejercicios"}.</p>
            <p className="text-muted text-sm mb-8">{logs.length} series registradas · guardado en tu historial</p>
            <button onClick={onClose} className="btn-primary" data-testid="live-finish">Cerrar</button>
          </div>
        ) : phase === "working" ? (
          !current ? (
            <div className="h-full flex items-center justify-center text-muted text-sm">
              Cargando ejercicio...
            </div>
          ) : (
          <div className="max-w-3xl mx-auto p-4 sm:p-8">
            <div className="text-center mb-6">
              <div className="text-xs uppercase tracking-widest text-muted mb-2">
                Ejercicio {exIdx + 1} de {totalExercises}
              </div>
              <div className="text-xs uppercase tracking-widest text-primary-app">
                Serie {setIdx + 1} de {totalSets}
              </div>
            </div>

            <div className="card-base overflow-hidden mb-6">
              {current.image_url && !imgBroken && (
                <div className="aspect-video bg-[#0A0A0A] overflow-hidden">
                  <img
                    src={current.image_url}
                    alt={current.exercise_name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                    onError={() => setImgBroken(true)}
                  />
                </div>
              )}
              <div className="p-6">
                <button
                  type="button"
                  onClick={() => setShowHowTo(true)}
                  className="w-full text-left group"
                  title="Ver cómo se hace"
                  data-testid="exercise-name-btn"
                >
                  <h2 className="font-display font-black uppercase text-3xl sm:text-4xl tracking-tighter mb-1 group-hover:text-primary-app transition-colors">
                    {current.exercise_name}
                  </h2>
                  <div className="text-[10px] uppercase tracking-widest text-muted group-hover:text-primary-app transition-colors mb-4">
                    Tocá para ver cómo se hace →
                  </div>
                </button>
                {(() => {
                  const extraTiles = (current.distance_m ? 1 : 0) + (current.duration_min ? 1 : 0);
                  const gridCols = ["grid-cols-3", "grid-cols-4", "grid-cols-5"][extraTiles];
                  return (
                <div className={`grid ${gridCols} gap-3`}>
                  <div className="bg-[#0A0A0A] border border-app p-4 text-center">
                    <div className="text-[10px] uppercase tracking-widest text-muted mb-1">Peso</div>
                    <div className="font-display font-black text-3xl text-primary-app">{current.weight_kg || 0}<span className="text-base text-muted"> kg</span></div>
                  </div>
                  <div className="bg-[#0A0A0A] border border-app p-4 text-center">
                    <div className="text-[10px] uppercase tracking-widest text-muted mb-1">Reps</div>
                    <div className="font-display font-black text-3xl">{current.reps || 0}</div>
                  </div>
                  {current.distance_m ? (
                    <div className="bg-[#0A0A0A] border border-app p-4 text-center" data-testid="working-distance">
                      <div className="text-[10px] uppercase tracking-widest text-muted mb-1">Distancia</div>
                      <div className="font-display font-black text-3xl" style={{ color: "#32D74B" }}>{current.distance_m}<span className="text-base text-muted"> m</span></div>
                    </div>
                  ) : null}
                  {current.duration_min ? (
                    <div className="bg-[#0A0A0A] border border-app p-4 text-center" data-testid="working-duration">
                      <div className="text-[10px] uppercase tracking-widest text-muted mb-1">Tiempo</div>
                      <div className="font-display font-black text-3xl" style={{ color: "#FFD60A" }}>{current.duration_min}<span className="text-base text-muted"> min</span></div>
                    </div>
                  ) : null}
                  <div className="bg-[#0A0A0A] border border-app p-4 text-center">
                    <div className="text-[10px] uppercase tracking-widest text-muted mb-1">Series</div>
                    <div className="font-display font-black text-3xl" style={{ color: "#007AFF" }}>{setIdx + 1}/{totalSets}</div>
                  </div>
                </div>
                  );
                })()}
                {(() => {
                  const pace = paceLabel(current.distance_m, current.duration_min);
                  if (!pace) return null;
                  return (
                    <div className="mt-3 bg-gradient-to-r from-[#32D74B]/15 to-transparent border border-[#32D74B]/30 p-3 flex items-center justify-between" data-testid="working-pace">
                      <span className="text-[10px] uppercase tracking-widest text-muted">Ritmo objetivo</span>
                      <span className="font-display font-black text-lg" style={{ color: "#32D74B" }}>{pace}</span>
                    </div>
                  );
                })()}
                <div className="mt-4 pt-4 border-t border-app grid grid-cols-2 gap-3 text-xs uppercase tracking-widest text-muted">
                  <div>Micro pausa: <span className="text-white font-semibold">{micro}s</span></div>
                  <div>Macro pausa: <span className="text-white font-semibold">{macro}s</span></div>
                </div>
              </div>
            </div>

            <button onClick={startSetLog} className="btn-primary w-full !py-4 text-base flex items-center justify-center gap-2" data-testid="complete-set-btn">
              <CheckCircle size={22} weight="bold" /> Completé la serie
            </button>
          </div>
          )
        ) : phase === "logging" ? (
          !current ? null : (
          <div className="max-w-2xl mx-auto p-4 sm:p-8">
            <div className="text-center mb-6">
              <div className="text-xs uppercase tracking-widest text-muted mb-2">Registrá la serie</div>
              <h2 className="font-display font-black uppercase text-3xl tracking-tighter">
                {current.exercise_name} · Serie {setIdx + 1}/{totalSets}
              </h2>
            </div>

            <div className="card-base p-6 mb-4" data-testid="rpe-panel">
              <div className="text-xs uppercase tracking-widest text-muted mb-3">RPE (1-10) — ¿Qué tan duro fue?</div>
              <div className="grid grid-cols-5 sm:grid-cols-10 gap-2 mb-3">
                {rpeButtons.map((n) => (
                  <button
                    key={n}
                    onClick={() => setRpe(n)}
                    className={`aspect-square font-display font-black text-xl transition-all border ${
                      rpe === n ? "border-white text-white" : "border-[#262626] text-muted hover:text-white hover:border-white"
                    }`}
                    style={rpe === n ? { backgroundColor: rpeColor(n), borderColor: rpeColor(n) } : {}}
                    data-testid={`rpe-${n}`}
                  >
                    {n}
                  </button>
                ))}
              </div>
              {rpe && (
                <div className="text-sm text-muted text-center" data-testid="rpe-label">
                  {rpe} — {RPE_LABELS[rpe]}
                </div>
              )}
            </div>

            <div className="card-base p-6 mb-6">
              <label className="text-xs uppercase tracking-widest text-muted block mb-3">Observaciones (opcional)</label>
              <textarea
                className="input-base"
                rows="3"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Ej: Técnica buena, dolor leve rodilla derecha..."
                data-testid="set-notes"
              />
            </div>

            <button onClick={confirmSetLog} className="btn-primary w-full !py-4 text-base flex items-center justify-center gap-2" data-testid="confirm-set-log">
              <FloppyDisk size={20} weight="bold" /> Guardar y continuar
            </button>
          </div>
          )
        ) : (
          /* Rest phase */
          <LiveSessionRestPhase
            phase={phase}
            micro={micro}
            macro={macro}
            remaining={remaining}
            current={current}
            setIdx={setIdx}
            totalSets={totalSets}
            exercises={exercises}
            exIdx={exIdx}
            paused={paused}
            onTogglePause={() => setPaused(!paused)}
            onSkip={skipRest}
          />
        )}
      </div>
      {showHowTo && current && (
        <ExerciseHowToModal
          exercise={current}
          onClose={() => setShowHowTo(false)}
        />
      )}
    </div>
  );
}
